import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button, IconButton } from "@material-tailwind/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { SpeakerContent, BioType } from "./SpeakerCardList";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  person: SpeakerContent;
  activeTab: "biography" | "abstract";
  setActiveTab: (tab: "biography" | "abstract") => void;
}

const SpeakerSidebar: React.FC<SidebarProps> = ({ isOpen, onClose, person, activeTab, setActiveTab }) => {
  const renderContent = (content?: BioType[]) => {
    if (!content || content.length === 0) {
      return <p className="text-gray-500 italic">No content available.</p>;
    }
    return content.map((b, i) => <p key={i} className="text-gray-700 mb-2">{b.children[0].text}</p>);
  };

  const imgUrl = person.image?.asset
    ? `https://cdn.sanity.io/images/79vgbaej/production/${person.image.asset._ref.slice(6, -4)}.${person.image.asset._ref.slice(-3)}`
    : "";

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-40"
            onClick={onClose}
          />

          {/* Sidebar */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed top-0 right-0 h-full w-full max-w-full md:w-[35vw] bg-gray-100 shadow-2xl z-50 flex flex-col p-4 md:p-6 overflow-y-auto"
          >
            {/* Close button */}
            <div className="flex justify-end">
              <IconButton variant="text" color="gray" onClick={onClose} className="hover:bg-gray-100">
                <XMarkIcon className="h-6 w-6" />
              </IconButton>
            </div>

            {/* Person Image & Name */}
            {imgUrl && <img src={imgUrl} alt={person.name} className="w-24 h-28 object-cover rounded-xl shadow-md mb-4 mx-auto" />}
            <h2 className="text-2xl font-bold text-center mb-2">{person.name}</h2>
            <p className="text-center text-gray-600 mb-4">{person.Role} - {person.university}</p>

            {/* Tab Buttons */}
            <div className="flex gap-4 mb-6 justify-center">
              <Button
                color={activeTab === "biography" ? "red" : "gray"}
                variant={activeTab === "biography" ? "filled" : "outlined"}
                onClick={() => setActiveTab("biography")}
                className="rounded-full px-6"
              >
                Biography
              </Button>
              <Button
                color={activeTab === "abstract" ? "red" : "gray"}
                variant={activeTab === "abstract" ? "filled" : "outlined"}
                onClick={() => setActiveTab("abstract")}
                className="rounded-full px-6"
              >
                Abstract
              </Button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1">
              {activeTab === "biography" ? (
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-red-500">Biography</h3>
                  {renderContent(person.Biography)}
                </div>
              ) : (
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-red-500">Abstract</h3>
                  {person.AbstractTitle && (
                    <h4 className="text-md font-semibold mb-4 text-gray-800 border-l-4 border-red-400 pl-3 py-1">
                      {person.AbstractTitle}
                    </h4>
                  )}
                  {renderContent(person.Abstract)}
                </div>
              )}
            </div>

            {/* Close Button at Bottom */}
            <Button color="red" variant="filled" className="mt-6 rounded-full" onClick={onClose}>
              Close
            </Button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SpeakerSidebar;